import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, scopedRules, templates } from '../lib/api.js';
import type { ClientRulesResult, Rule, RuleTarget, UserRulesResult } from '../lib/types.js';
import { DeleteRuleDialog } from './DeleteRuleDialog.jsx';
import { RuleEditor } from './RuleEditor.jsx';
import { RuleTable } from './RuleTable.jsx';
import './RulesDialog.css';

/** Bersagli che hanno una pagina propria qui: il template ha la sua sezione. */
export type ScopedTarget = Extract<RuleTarget, { scope: 'client' | 'user' }>;

/**
 * Regole di un'organizzazione o di una singola utenza (§6.4).
 *
 * Mostra sempre **anche** quello che si eredita, in sola lettura. Senza, un
 * elenco vuoto sembrerebbe "nessun permesso", mentre il soggetto ha tutti
 * quelli del livello sopra: è la lettura sbagliata che porta a riscrivere qui
 * regole che già esistono, e a sovrapposizioni difficili da districare dopo.
 */
export function RulesDialog({ target, onClose }: { target: ScopedTarget; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Rule | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Rule | null>(null);

  const proprie = useQuery<ClientRulesResult | UserRulesResult>({
    queryKey:
      target.scope === 'client'
        ? ['admin', 'clients', target.clientId, 'rules']
        : ['admin', 'clients', target.clientId, 'users', target.userId, 'rules'],
    queryFn: () =>
      target.scope === 'client'
        ? scopedRules.forClient(target.clientId)
        : scopedRules.forUser(target.clientId, target.userId),
  });

  // Il livello sopra: per un'utenza sono le regole dell'organizzazione, e sopra
  // ancora quelle del template che l'organizzazione usa.
  const dellOrganizzazione = useQuery<ClientRulesResult>({
    queryKey: ['admin', 'clients', target.clientId, 'rules'],
    queryFn: () => scopedRules.forClient(target.clientId),
    enabled: target.scope === 'user',
  });

  const templateId =
    target.scope === 'client'
      ? ((proprie.data as ClientRulesResult | undefined)?.client.templateId ?? null)
      : (dellOrganizzazione.data?.client.templateId ?? null);

  const delTemplate = useQuery({
    queryKey: ['admin', 'templates', templateId, 'rules'],
    queryFn: () => templates.rules(templateId!),
    enabled: templateId !== null,
  });

  const ereditate: Rule[] = [
    ...(target.scope === 'user' ? (dellOrganizzazione.data?.rules ?? []) : []),
    ...(delTemplate.data?.rules ?? []),
  ];

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
  };

  const regole = proprie.data?.rules ?? [];

  return (
    <div className="picker" role="dialog" aria-modal="true" aria-label="Regole">
      <div className="picker__box card rulesd">
        <header className="picker__head">
          <div>
            <h3>Regole · {target.label}</h3>
            <p className="rulesd__lead">
              {target.scope === 'user'
                ? 'Valgono solo per questa utenza dentro questa organizzazione, e vincono su tutto il resto.'
                : "Valgono per tutte le utenze dell'organizzazione e vincono sul template."}
            </p>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Chiudi
          </button>
        </header>

        <div className="rulesd__body">
          {proprie.isPending ? (
            <p className="rulesd__state">Caricamento…</p>
          ) : proprie.isError ? (
            <div className="alert alert--error">
              {proprie.error instanceof ApiError
                ? proprie.error.message
                : 'Caricamento non riuscito.'}
            </div>
          ) : (
            <>
              <div className="rulesd__actions">
                <button type="button" className="btn" onClick={() => setEditing('new')}>
                  Nuova regola
                </button>
              </div>

              {regole.length === 0 ? (
                <p className="rulesd__state">
                  Nessuna deroga: valgono solo le regole ereditate, qui sotto.
                </p>
              ) : (
                <RuleTable rules={regole} onEdit={setEditing} onDelete={setDeleting} />
              )}

              <details className="rulesd__inherited">
                <summary>
                  Regole ereditate ({ereditate.length}) — sola lettura
                </summary>
                {ereditate.length === 0 ? (
                  <p className="rulesd__state">
                    Nessuna: senza regole proprie il soggetto non vede nulla (deny by default).
                  </p>
                ) : (
                  <RuleTable rules={ereditate} showScope />
                )}
              </details>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <RuleEditor
          target={target}
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void proprie.refetch();
            refresh();
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteRuleDialog
          target={target}
          rule={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void proprie.refetch();
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
